// Newsletter signup: an inline section, a pop-up a few seconds after the page
// loads, or both. Signups go straight to Kit (see kit.ts).
import { FormEvent, lazy, Suspense, useEffect, useId, useState } from 'react';
import { FaEye, FaPen } from 'react-icons/fa';
import { FieldSpec, useSite } from '../content/context';
import { Section } from '../content/layout';
import { ActionButton, EditableText } from '../edit/primitives';
import { Paragraphs } from './CustomSections';
import { kitFormAction, subscribe, SubscribeResult } from './kit';

// The modal's code is only downloaded when the pop-up is about to show.
const NewsletterModal = lazy(() => import('./NewsletterModal'));

export type NewsletterDisplay = 'popup' | 'inline' | 'both';
export const newsletterDisplay = (section: Section): NewsletterDisplay => section.display ?? 'inline';
export const popupDelaySeconds = (section: Section) => Math.min(60, Math.max(0, Math.round(section.popupDelay ?? 5)));

const EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;

// Visitors who close the pop-up don't see it again for a while; subscribers never do.
const DISMISSED_KEY = 'arcflash-newsletter-dismissed-until';
const SUBSCRIBED_KEY = 'arcflash-newsletter-subscribed';
export const DISMISS_DAYS = 14;

export function shouldShowPopup(now = Date.now(), storage: Pick<Storage, 'getItem'> = window.localStorage): boolean {
  try {
    if (storage.getItem(SUBSCRIBED_KEY)) return false;
    return !(Number(storage.getItem(DISMISSED_KEY)) > now);
  } catch {
    return true; // storage blocked (private mode): show once per visit
  }
}

export function rememberDismissed(now = Date.now(), storage: Pick<Storage, 'setItem'> = window.localStorage) {
  try {
    storage.setItem(DISMISSED_KEY, String(now + DISMISS_DAYS * 24 * 60 * 60 * 1000));
  } catch {
    // ignore
  }
}

export function rememberSubscribed(storage: Pick<Storage, 'setItem'> = window.localStorage) {
  try {
    storage.setItem(SUBSCRIBED_KEY, '1');
  } catch {
    // ignore
  }
}

interface FormProps {
  section: Section;
  update?: (patch: Partial<Section>) => void;
  /** Shows the form without letting it submit (editor preview). */
  readOnly?: boolean;
  onSubscribed?: () => void;
}

/** The email box, button and status messages, shared by the section and the pop-up. */
export function NewsletterForm({ section, update, readOnly, onSubscribed }: FormProps) {
  const { edit } = useSite();
  const inputId = useId();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SubscribeResult>();
  const editing = Boolean(edit) && Boolean(update) && !readOnly;
  const inactive = Boolean(edit) || readOnly;
  const configured = Boolean(kitFormAction(section.formId));
  const buttonLabel = section.buttonLabel || 'Subscribe';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (inactive) return; // never submits from the editor
    if (!EMAIL.test(email.trim())) {
      setResult({ kind: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setBusy(true);
    setResult(undefined);
    const outcome = await subscribe(section.formId, email);
    setBusy(false);
    setResult(outcome);
    if (outcome.kind === 'success') {
      setEmail('');
      rememberSubscribed();
      onSubscribed?.();
    }
  };

  if (result?.kind === 'success') {
    return (
      <p className="af-newsletter-status" role="status">
        Almost done! Check your inbox and confirm your subscription.
      </p>
    );
  }

  return (
    <>
      <form className="af-newsletter-form" onSubmit={submit} noValidate>
        <label htmlFor={inputId} className="af-visually-hidden">
          Email address
        </label>
        <input
          id={inputId}
          type="email"
          name="email_address"
          className="form-control form-control-lg"
          placeholder="Your email address"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy || inactive}
          aria-describedby={`${inputId}-status`}
        />
        {editing ? (
          <span className="btn btn-lg af-button">
            <EditableText label="Button text" value={buttonLabel} onChange={(v) => update?.({ buttonLabel: v || 'Subscribe' })} />
          </span>
        ) : (
          <button type="submit" className="btn btn-lg af-button" disabled={busy || inactive}>
            {busy ? 'Subscribing…' : buttonLabel}
          </button>
        )}
      </form>
      <div id={`${inputId}-status`} className="af-newsletter-status" role="status" aria-live="polite">
        {result?.kind === 'error' ? <span>{result.message}</span> : null}
        {result?.kind === 'verify' ? (
          <span>
            One more step:{' '}
            <a href={result.url} target="_blank" rel="noopener noreferrer">
              confirm you're not a robot
            </a>
            .
          </span>
        ) : null}
        {edit && !configured ? <span>Connect a Kit form with the pencil button to turn this on.</span> : null}
      </div>
    </>
  );
}

const DISPLAY_LABELS: Record<NewsletterDisplay, string> = {
  popup: 'Pop-up only',
  inline: 'On the page only',
  both: 'Pop-up and on the page',
};

const settingsFields: FieldSpec[] = [
  {
    name: 'display',
    label: 'Where should it appear?',
    type: 'select',
    required: true,
    options: (Object.keys(DISPLAY_LABELS) as NewsletterDisplay[]).map((value) => ({ value, label: DISPLAY_LABELS[value] })),
  },
  {
    name: 'popupDelay',
    label: 'Pop-up delay (seconds)',
    placeholder: '5',
    hint: `How long after the page loads the pop-up appears. Visitors who close it won't see it again for ${DISMISS_DAYS} days, and subscribers never do.`,
    validate: (v) => (!v || (/^\d{1,2}$/.test(v) && Number(v) <= 60) ? undefined : 'Enter a number of seconds from 0 to 60.'),
  },
  {
    name: 'formId',
    label: 'Kit form number',
    required: true,
    placeholder: '10144970',
    hint: 'In Kit: open the form, choose Publish → HTML, and copy the number from action="https://app.kit.com/forms/NUMBER/subscriptions".',
    validate: (v) => (kitFormAction(v) ? undefined : 'Enter just the number, e.g. 10144970.'),
  },
];

interface Props {
  section: Section;
  update: (patch: Partial<Section>) => void;
}

/** The newsletter as a page section. In the editor it also previews the pop-up. */
export function NewsletterSection({ section, update }: Props) {
  const { edit } = useSite();
  const [previewing, setPreviewing] = useState(false);
  const display = newsletterDisplay(section);
  const configured = Boolean(kitFormAction(section.formId));

  if (!edit && (!configured || display === 'popup')) return null;

  const editSettings = async () => {
    if (!edit) return;
    const res = await edit.openForm({
      title: 'Newsletter settings',
      fields: settingsFields,
      values: { display, popupDelay: String(popupDelaySeconds(section)), formId: section.formId },
    });
    if (!res || !('values' in res)) return;
    const next = res.values.display as NewsletterDisplay;
    update({
      formId: res.values.formId.trim(),
      display: next in DISPLAY_LABELS ? next : 'inline',
      popupDelay: res.values.popupDelay ? Number(res.values.popupDelay) : 5,
    });
  };

  return (
    <div className="af-newsletter af-embed">
      {edit && display !== 'inline' ? (
        <p className="af-newsletter-note">
          {display === 'popup' ? 'Visitors see this as a pop-up' : 'Visitors also see this as a pop-up'} {popupDelaySeconds(section)} seconds after
          the page loads{display === 'popup' ? ', not on the page' : ''}.
        </p>
      ) : null}
      <Paragraphs text={section.text} onChange={(text) => update({ text })} />
      <NewsletterForm section={section} update={update} />
      {edit ? (
        <div className="af-actions">
          {display !== 'inline' ? <ActionButton label="Preview pop-up" icon={<FaEye />} showLabel onClick={() => setPreviewing(true)} /> : null}
          <ActionButton label="Newsletter settings" icon={<FaPen />} showLabel onClick={editSettings} />
        </div>
      ) : null}
      {previewing ? (
        <Suspense fallback={null}>
          <NewsletterModal section={section} show preview onHide={() => setPreviewing(false)} />
        </Suspense>
      ) : null}
    </div>
  );
}

/** Shows the newsletter pop-up a few seconds after the public page loads. */
export function NewsletterPopup() {
  const { data, edit } = useSite();
  const [show, setShow] = useState(false);
  const section = data.layout.sections.find(
    (s) => s.type === 'newsletter' && !s.hidden && newsletterDisplay(s) !== 'inline' && kitFormAction(s.formId),
  );
  const delay = section ? popupDelaySeconds(section) : 0;

  useEffect(() => {
    if (edit || !section) return;
    const timer = window.setTimeout(() => {
      if (shouldShowPopup()) setShow(true);
    }, delay * 1000);
    return () => window.clearTimeout(timer);
  }, [edit, section, delay]);

  if (edit || !section || !show) return null;
  return (
    <Suspense fallback={null}>
      <NewsletterModal
        section={section}
        show={show}
        onHide={() => {
          rememberDismissed();
          setShow(false);
        }}
      />
    </Suspense>
  );
}
